import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useOpenedGoal } from "@/lib/metrica";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Check, Loader2, ShieldCheck, Calendar, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import PastDueRetryCard from "@/components/PastDueRetryCard";
import DirectPaymentForm from "@/components/DirectPaymentForm";

/**
 * Abonelik / ödeme başlatma ekranı.
 *
 * 1. `/paytr/plans` public endpoint'inden plan bilgilerini çeker.
 * 2. Kullanıcı bir plan seçer.
 * 3. `POST /paytr/initiate` → dönen `iframe_url` güvenli bir <iframe>
 *    içinde gösterilir; PayTR ödeme sayfası burada render olur.
 * 4. PayTR sonucu backend callback ile işler; frontend `merchant_oid`'yi
 *    başarı/başarısız sayfaları için hatırlar.
 */
export default function Subscription() {
  useOpenedGoal("subscription_page_opened", true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [payment, setPayment] = useState(null); // { merchant_oid, iframe_url, plan }
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/paytr/plans");
        setPlans(data);
      } catch {
        toast.error("Plan bilgileri yüklenemedi. Lütfen tekrar deneyin.");
      }
      // Ayrıca abonelik durumu — past-due retry paneli için.
      try {
        const { data: sub } = await api.get("/subscription");
        setSubscription(sub);
      } catch {
        /* trial kullanıcı da fetch edebilir; hata olursa yut */
      }
    })();
  }, []);

  const startPayment = (planCode) => {
    // Faz 2D — iFrame akışı DEPRECATED.  Kullanıcı plan seçince
    // doğrudan kart formuna geçer (Penz Direkt API akışı).
    const planTotal = planCode === "yearly"
      ? plans?.yearly?.total_amount_tl
      : plans?.monthly?.total_amount_tl;
    setPayment({ plan: planCode, total_amount_tl: planTotal });
  };

  const backToPlans = () => setPayment(null);

  // Hesap durumuna göre üst bilgilendirme metni.  Mevcut ödeme akışı ve
  // plan kartları aynen korunur; yalnızca kullanıcıya neden bu ekranda
  // olduğunu anlatan başlık + açıklama değiştirilir.  active +
  // trial_expired için MEVCUT metinlere DOKUNULMAZ.
  const acc = user?.account_status;
  let headerTitle = "Abonelik";
  let headerSubtitle = user?.trial_expired
    ? "Ücretsiz deneme süreniz doldu. Devam etmek için bir plan seçin."
    : "Aylık veya yıllık plan seçerek Penz aboneliğinizi başlatın.";
  if (acc === "passive") {
    headerTitle = "Hesabınız pasif durumda";
    headerSubtitle =
      "Aboneliğinizi yenileyerek Penz'i kullanmaya devam edebilirsiniz.";
  } else if (acc === "archived") {
    headerTitle = "Hesabınız arşivde";
    headerSubtitle =
      "Aboneliğinizi yenileyerek hesabınızı yeniden aktif hale getirebilir ve Penz'i kullanmaya devam edebilirsiniz.";
  }

  return (
    <div className="space-y-6" data-testid="subscription-page">
      <StickyPageHead>
        <PageHeader
          title={headerTitle}
          subtitle={headerSubtitle}
          action={
            payment ? (
              <Button
                variant="outline"
                onClick={backToPlans}
                data-testid="subscription-back-to-plans"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Planları Gör
              </Button>
            ) : null
          }
          testId={
            acc === "passive"
              ? "subscription-header-passive"
              : acc === "archived"
                ? "subscription-header-archived"
                : "subscription-header-default"
          }
        />
      </StickyPageHead>

      {/* --- Past-Due / Retry panel — ödeme başarısız hesaplar için --- */}
      {!payment && subscription?.effective_status === "payment_pending" && (
        <PastDueRetryCard
          subscription={subscription}
          onRefresh={async () => {
            try {
              const { data: sub } = await api.get("/subscription");
              setSubscription(sub);
            } catch { /* yut */ }
          }}
        />
      )}

      {/* --- Aşama 1: Plan seçim kartları --- */}
      {!payment && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <PlanCard
            code="monthly"
            title="Aylık Plan"
            hint="Kısa dönem denemek için ideal"
            price={plans?.monthly?.base_amount_tl}
            total={plans?.monthly?.total_amount_tl}
            loading={selecting === "monthly"}
            onSelect={() => startPayment("monthly")}
            testId="plan-monthly"
          />
          <PlanCard
            code="yearly"
            title="Yıllık Plan"
            hint="2 ay bedava — en avantajlı"
            price={plans?.yearly?.base_amount_tl}
            total={plans?.yearly?.total_amount_tl}
            highlighted
            loading={selecting === "yearly"}
            onSelect={() => startPayment("yearly")}
            testId="plan-yearly"
          />
        </div>
      )}

      {/* --- Aşama 2: Direkt API Kart Formu (iFrame DEPRECATED) --- */}
      {payment && (
        <DirectPaymentForm
          plan={payment.plan}
          totalTl={payment.total_amount_tl}
          onBack={backToPlans}
        />
      )}

      {/* --- Alt bilgi --- */}
      {!payment && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InfoCard icon={ShieldCheck} title="Güvenli Ödeme">
            Tüm ödemeler PayTR altyapısı üzerinden 3D Secure ile alınır. Kart
            bilgileriniz Penz tarafında saklanmaz.
          </InfoCard>
          <InfoCard icon={Calendar} title="İptal Esnekliği">
            Aylık plan bir sonraki dönem sonunda, yıllık plan bir yıl sonunda
            yenilenir. İstediğiniz zaman iptal edebilirsiniz.
          </InfoCard>
          <InfoCard icon={Check} title="Tüm Modüller Dahil">
            Müşteri, iş, tedarikçi, satın alma, gider, kasa ve rapor
            modüllerinin tamamı abonelik kapsamındadır.
          </InfoCard>
        </div>
      )}

      {!user?.trial_expired && (
        <div className="text-center">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            data-testid="subscription-back-to-app"
          >
            Uygulamaya geri dön
          </Button>
        </div>
      )}
    </div>
  );
}

function PlanCard({ code, title, hint, price, total, highlighted, loading, onSelect, testId }) {
  const isReady = price !== undefined && total !== undefined;
  return (
    <div
      className={
        "relative bg-white rounded-xl border p-6 transition-all " +
        (highlighted
          ? "border-slate-900 shadow-sm"
          : "border-slate-200 hover:border-slate-300")
      }
      data-testid={testId}
    >
      {highlighted && (
        <div className="absolute -top-3 right-6 text-[10px] uppercase tracking-[0.2em] bg-slate-900 text-white px-3 py-1 rounded-full">
          Önerilen
        </div>
      )}
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
        {title}
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="font-display text-4xl font-bold tracking-tight text-slate-900 tabular-nums">
          {isReady ? Number(price).toLocaleString("tr-TR") : "…"}
        </span>
        <span className="text-slate-500 text-sm">TL</span>
        <span className="text-xs text-slate-400 ml-1">+ KDV</span>
      </div>
      <div className="text-sm text-slate-600 mt-1.5">
        {isReady ? `KDV dahil ${Number(total).toLocaleString("tr-TR")} TL` : "\u00A0"}
      </div>
      <p className="text-sm text-slate-500 mt-2">{hint}</p>

      <ul className="mt-5 space-y-2 text-sm text-slate-700">
        {[
          "Sınırsız müşteri ve iş kaydı",
          "Tedarikçi, satın alma, gider modülleri",
          "Kasa yönetimi ve raporlar",
          "PDF cari ekstre ve yedekleme",
        ].map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={onSelect}
        disabled={!isReady || loading}
        className={
          "w-full mt-6 h-11 rounded-lg " +
          (highlighted
            ? "bg-slate-900 hover:bg-slate-800 text-white"
            : "bg-white border border-slate-900 text-slate-900 hover:bg-slate-50")
        }
        data-testid={`select-plan-${code}-button`}
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Hazırlanıyor…</>
        ) : (
          <>Bu Planı Seç</>
        )}
      </Button>
    </div>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center mb-3">
        <Icon className="w-4 h-4 text-slate-700" />
      </div>
      <div className="font-display text-sm font-semibold text-slate-900">{title}</div>
      <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{children}</p>
    </div>
  );
}
